import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { AppConfig } from '@fleet/shared';

/**
 * How to write money, and which clock the counter is on.
 *
 * A static file served from the root, not a per-client build artefact. It used
 * to be `client.json`, copied in from `clients/<slug>/` so that one image
 * carried one business's settings — which the SaaS pivot made wrong, because
 * one build now serves every tenant. Everything genuinely per-client lives on
 * the tenant row and arrives through the session.
 */

type ConfigState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly config: AppConfig }
  | { readonly kind: 'failed'; readonly detail: string };

const AppConfigContext = createContext<AppConfig | null>(null);

export function useAppConfig(): AppConfig {
  const config = useContext(AppConfigContext);
  if (config === null) {
    throw new Error('useAppConfig used outside AppConfigProvider');
  }
  return config;
}

async function loadConfig(): Promise<AppConfig> {
  const response = await fetch('/app-config.json', { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`app-config.json returned ${String(response.status)}`);
  }
  return (await response.json()) as AppConfig;
}

export function AppConfigProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<ConfigState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void loadConfig().then(
      (config) => {
        if (!cancelled) setState({ kind: 'ready', config });
      },
      (error: unknown) => {
        if (!cancelled) {
          setState({ kind: 'failed', detail: error instanceof Error ? error.message : 'unknown' });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return <div className="min-h-dvh bg-paper" aria-busy="true" />;
  }

  if (state.kind === 'failed') {
    return (
      <div className="min-h-dvh bg-paper p-8">
        <div className="max-w-lg border border-rule bg-panel p-5">
          <h1 className="text-heading font-semibold">This install has no settings file</h1>
          <p className="mt-2 text-ink-muted">
            The back office could not read <span className="identifier">app-config.json</span>, so it
            does not know which business it belongs to. Run{' '}
            <span className="identifier">make config CLIENT=&lt;name&gt;</span> and reload.
          </p>
          <p className="mt-3 text-small text-ink-faint">{state.detail}</p>
        </div>
      </div>
    );
  }

  return (
    <AppConfigContext.Provider value={state.config}>{children}</AppConfigContext.Provider>
  );
}
